import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Box, Check, Code2, Gauge, LayoutDashboard, Plus, Search, Table2, Terminal, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import FdeSelect from '@/frontend/features/Fde/FdeSelect'
import type { Draft, FieldKind, Operator, PickField, SourceChoice } from './builder'
import { NO_VALUE, OPERATORS, SOURCE_CHOICES, fieldKind } from './builder'
import type { DashboardAnalysis, ForgeTable, ObjectTypeOption, SourceFilter } from './types'

type Copy = Record<string, string>

/** One step of the flow, drawn as a node with the line that leads to the next one. */
export function Node({ index, icon, title, summary, children, last }: {
  index: number; icon: ReactNode; title: string; summary?: string; children: ReactNode; last?: boolean
}) {
  return (
    <section className="relative grid grid-cols-[2.25rem_minmax(0,1fr)] gap-x-3">
      <div className="flex flex-col items-center">
        <span className="grid size-9 place-items-center rounded-xl border border-primary/30 bg-primary/10 text-primary">{icon}</span>
        {!last && <span className="mt-1 w-px flex-1 bg-border" aria-hidden />}
      </div>
      <div className={cn('@container grid min-w-0 gap-4 rounded-2xl border border-border bg-background p-4 shadow-xs @2xl:p-5', !last && 'mb-4')}>
        <header className="grid gap-0.5">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{index}</span>
          <h3 className="text-base font-semibold">{title}</h3>
          {summary && <p className="text-sm text-muted-foreground">{summary}</p>}
        </header>
        {children}
      </div>
    </section>
  )
}

export function Labeled({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="grid min-w-0 gap-1.5 text-sm">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  )
}

const CHOICE_ICON: Record<SourceChoice, ReactNode> = {
  objects: <Box className="size-4" aria-hidden />,
  table: <Table2 className="size-4" aria-hidden />,
  metric: <Gauge className="size-4" aria-hidden />,
  analysis: <LayoutDashboard className="size-4" aria-hidden />,
  python: <Code2 className="size-4" aria-hidden />,
  sql: <Terminal className="size-4" aria-hidden />,
}

export function SourceChoices({ value, onChange, copy }: { value: SourceChoice; onChange: (c: SourceChoice) => void; copy: Copy }) {
  return (
    <div className="grid gap-2 @md:grid-cols-2 @2xl:grid-cols-3" role="radiogroup" aria-label={copy.step_watch}>
      {SOURCE_CHOICES.map((choice) => (
        <button key={choice} type="button" role="radio" aria-checked={value === choice} onClick={() => onChange(choice)}
          className={cn('grid min-w-0 content-start gap-1 rounded-xl border p-3 text-left transition-colors',
            value === choice ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:border-foreground/20 hover:bg-accent/30')}>
          <span className={cn('flex items-center gap-2 text-sm font-medium', value === choice && 'text-primary')}>{CHOICE_ICON[choice]}{copy[`choice_${choice}`]}</span>
          {copy[`choice_${choice}_help`] && <span className="line-clamp-2 text-xs leading-snug text-muted-foreground">{copy[`choice_${choice}_help`]}</span>}
        </button>
      ))}
    </div>
  )
}

export function ObjectPicker({ options, value, onChange, copy }: {
  options: ObjectTypeOption[]; value: string; onChange: (id: string) => void; copy: Copy
}) {
  const [query, setQuery] = useState('')
  const shown = options.filter((o) => `${o.name} ${o.description ?? ''}`.toLowerCase().includes(query.trim().toLowerCase()))
  if (!options.length) return <p className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">{copy.no_objects}</p>
  return (
    <div className="grid gap-2">
      {options.length > 6 && <SearchBox value={query} onChange={setQuery} placeholder={copy.search_objects} />}
      <div className="grid gap-2 @2xl:grid-cols-2">
        {shown.map((o) => (
          <button key={o.id} type="button" disabled={!o.ready} onClick={() => onChange(o.id)} aria-pressed={value === o.id}
            className={cn('grid min-w-0 gap-0.5 rounded-xl border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
              value === o.id ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:border-foreground/20')}>
            <span className="flex items-center justify-between gap-2 text-sm font-medium">
              <span className="truncate">{o.name}</span>
              {value === o.id && <Check className="size-4 shrink-0 text-primary" aria-hidden />}
            </span>
            <span className="line-clamp-2 text-xs text-muted-foreground">
              {o.ready ? `${o.source?.fields.length ?? 0} ${copy.properties}${o.description ? ` · ${o.description}` : ''}` : o.reason}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <label className="relative block">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-9" />
    </label>
  )
}

export function TablePicker({ tables, value, onChange, copy, error }: {
  tables: ForgeTable[]; value: string; onChange: (name: string) => void; copy: Copy; error?: string | null
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(!value)
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase()
    const shown = tables.filter((t) => `${t.label} ${t.name}`.toLowerCase().includes(q))
    return shown.reduce<Record<string, ForgeTable[]>>((out, t) => ({ ...out, [t.layer_label]: [...(out[t.layer_label] ?? []), t] }), {})
  }, [tables, query])
  const picked = tables.find((t) => t.name === value)
  if (error) return <p className="rounded-xl border border-dashed border-destructive/40 p-4 text-sm text-destructive">{error}</p>
  if (!tables.length) return <p className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">{copy.no_tables}</p>
  if (picked && !open) {
    return (
      <div className="flex min-w-0 items-center justify-between gap-3 rounded-xl border border-border bg-muted/20 p-3">
        <div className="grid min-w-0 gap-0.5">
          <span className="truncate text-sm font-medium">{picked.label}</span>
          <span className="truncate font-mono text-xs text-muted-foreground">{picked.name} · {picked.layer_label}{picked.row_count != null ? ` · ${picked.row_count.toLocaleString('pt-BR')} ${copy.rows}` : ''}</span>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>{copy.change}</Button>
      </div>
    )
  }
  return (
    <div className="grid gap-2">
      <SearchBox value={query} onChange={setQuery} placeholder={copy.search_tables} />
      <div className="grid max-h-80 gap-3 overflow-y-auto rounded-xl border border-border p-2">
        {Object.entries(groups).map(([layer, items]) => (
          <div key={layer} className="grid gap-1">
            <span className="px-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{layer}</span>
            {items.map((t) => (
              <button key={t.name} type="button" onClick={() => { onChange(t.name); setOpen(false) }}
                className={cn('flex min-w-0 items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-accent/40', t.name === value && 'bg-primary/5 text-primary')}>
                <span className="min-w-0 truncate">{t.label} <span className="font-mono text-xs text-muted-foreground">{t.name}</span></span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{t.row_count != null ? t.row_count.toLocaleString('pt-BR') : ''}</span>
              </button>
            ))}
          </div>
        ))}
        {!Object.keys(groups).length && <p className="p-2 text-sm text-muted-foreground">{copy.nothing_found}</p>}
      </div>
    </div>
  )
}

/** Several fields at once, as chips: identity, what to carry, what counts as a change, group by. */
export function FieldChips({ fields, value, onChange, copy, limit }: {
  fields: PickField[]; value: string[]; onChange: (keys: string[]) => void; copy: Copy; limit?: number
}) {
  const [showTechnical, setShowTechnical] = useState(false)
  const visible = fields.filter((f) => showTechnical || !f.technical || value.includes(f.key))
  const toggle = (key: string) => onChange(value.includes(key) ? value.filter((k) => k !== key)
    : limit && value.length >= limit ? value : [...value, key])
  return (
    <div className="flex flex-wrap gap-1.5">
      {visible.map((f) => {
        const on = value.includes(f.key)
        return (
          <button key={f.key} type="button" aria-pressed={on} onClick={() => toggle(f.key)} title={f.field}
            className={cn('inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors',
              on ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:text-foreground')}>
            {on && <Check className="size-3" aria-hidden />}<span className="truncate">{f.label}</span>
          </button>
        )
      })}
      {fields.some((f) => f.technical) && (
        <button type="button" onClick={() => setShowTechnical((v) => !v)} className="px-1 text-xs text-muted-foreground underline-offset-2 hover:underline">
          {showTechnical ? copy.hide_technical : copy.show_technical}
        </button>
      )}
    </div>
  )
}

/** Columns that only exist after the analysis runs: typed, or picked from the last simulation. */
export function ColumnTags({ value, onChange, suggestions, copy }: {
  value: string[]; onChange: (v: string[]) => void; suggestions: string[]; copy: Copy
}) {
  const [typed, setTyped] = useState('')
  const add = (name: string) => {
    const clean = name.trim()
    if (clean && /^[A-Za-z_][A-Za-z0-9_]*$/.test(clean) && !value.includes(clean)) onChange([...value, clean])
    setTyped('')
  }
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {value.map((name) => (
          <span key={name} className="inline-flex items-center gap-1 rounded-full border border-primary bg-primary/10 px-2.5 py-1 font-mono text-xs text-primary">
            {name}<button type="button" aria-label={`${copy.remove} ${name}`} onClick={() => onChange(value.filter((v) => v !== name))}><X className="size-3" aria-hidden /></button>
          </span>
        ))}
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={copy.column_placeholder}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(typed) } }} onBlur={() => typed && add(typed)}
          className="h-8 w-44 font-mono text-xs" />
      </div>
      {suggestions.filter((s) => !value.includes(s)).length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span>{copy.from_simulation}</span>
          {suggestions.filter((s) => !value.includes(s)).map((s) => (
            <button key={s} type="button" onClick={() => add(s)} className="rounded-full border border-dashed border-border px-2 py-0.5 font-mono hover:text-foreground">+ {s}</button>
          ))}
        </div>
      )}
    </div>
  )
}

function defaultValue(op: Operator, kind: FieldKind): unknown {
  if (NO_VALUE.includes(op)) return null
  if (op === 'in' || op === 'not_in') return []
  if (op === 'between') return ['', '']
  if (op === 'within_days' || op === 'older_than_days') return 30
  return kind === 'number' ? '' : ''
}

function coerce(value: string, kind: FieldKind): unknown {
  if (kind === 'number' && value.trim() !== '' && !Number.isNaN(Number(value))) return Number(value)
  return value
}

/** Conditions on the source's fields, each one with operators that fit its type. */
export function FilterBuilder({ fields, value, onChange, copy }: {
  fields: PickField[]; value: SourceFilter[]; onChange: (f: SourceFilter[]) => void; copy: Copy
}) {
  const usable = fields.filter((f) => !f.technical)
  const update = (index: number, patch: Partial<SourceFilter>) => onChange(value.map((f, i) => (i === index ? { ...f, ...patch } : f)))
  const add = () => {
    const first = usable[0]
    if (!first) return
    const kind = fieldKind(first.type)
    const op = OPERATORS[kind][0]
    onChange([...value, { field: first.field, label: first.label, type: first.type, op, value: defaultValue(op, kind) }])
  }
  return (
    <div className="grid gap-2">
      {value.map((filter, index) => {
        const field = fields.find((f) => f.field === filter.field)
        const kind = fieldKind(field?.type ?? filter.type)
        return (
          <div key={index} className="grid items-start gap-2 rounded-xl border border-border bg-muted/10 p-2 @lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.2fr)_auto]">
            <FdeSelect value={filter.field} ariaLabel={copy.filter_field} required size="sm"
              options={usable.map((f) => ({ value: f.field, label: f.label }))}
              onValueChange={(name) => {
                const next = fields.find((f) => f.field === name)
                const nextKind = fieldKind(next?.type)
                const op = nextKind === kind && OPERATORS[nextKind].includes(filter.op) ? filter.op : OPERATORS[nextKind][0]
                update(index, { field: name, label: next?.label, type: next?.type, op, value: defaultValue(op, nextKind) })
              }} />
            <FdeSelect value={filter.op} ariaLabel={copy.filter_op} required size="sm"
              options={OPERATORS[kind].map((op) => ({ value: op, label: copy[`op_${op}_short`] ?? copy[`op_${op}`] ?? op }))}
              onValueChange={(op) => update(index, { op: op as Operator, value: defaultValue(op as Operator, kind) })} />
            <FilterValue filter={filter} kind={kind} copy={copy} onChange={(v) => update(index, { value: v })} />
            <Button size="icon" variant="ghost" className="size-8" aria-label={copy.remove} onClick={() => onChange(value.filter((_, i) => i !== index))}>
              <X className="size-4" aria-hidden />
            </Button>
          </div>
        )
      })}
      <div>
        <Button size="sm" variant="outline" onClick={add} disabled={!usable.length}><Plus className="size-4" aria-hidden />{copy.add_filter}</Button>
      </div>
    </div>
  )
}

function FilterValue({ filter, kind, copy, onChange }: { filter: SourceFilter; kind: FieldKind; copy: Copy; onChange: (v: unknown) => void }) {
  if (NO_VALUE.includes(filter.op)) return <span className="hidden @lg:block" />
  if (filter.op === 'in' || filter.op === 'not_in') {
    const list = Array.isArray(filter.value) ? filter.value.map(String) : []
    return <Input className="h-8" value={list.join(', ')} placeholder={copy.values_placeholder}
      onChange={(e) => onChange(e.target.value.split(',').map((v) => v.trim()).filter(Boolean).map((v) => coerce(v, kind)))} />
  }
  if (filter.op === 'between') {
    const [a, b] = Array.isArray(filter.value) ? filter.value : ['', '']
    return (
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1.5">
        <Input className="h-8" type={kind === 'number' ? 'number' : 'text'} value={String(a ?? '')} onChange={(e) => onChange([coerce(e.target.value, kind), b])} />
        <span className="text-xs text-muted-foreground">{copy.and}</span>
        <Input className="h-8" type={kind === 'number' ? 'number' : 'text'} value={String(b ?? '')} onChange={(e) => onChange([a, coerce(e.target.value, kind)])} />
      </div>
    )
  }
  if (filter.op === 'within_days' || filter.op === 'older_than_days') {
    return (
      <div className="flex items-center gap-1.5">
        <Input className="h-8 w-24" type="number" min={1} value={String(filter.value ?? '')} onChange={(e) => onChange(Number(e.target.value))} />
        <span className="text-xs text-muted-foreground">{copy.days}</span>
      </div>
    )
  }
  if (filter.op === 'before' || filter.op === 'after') {
    return <Input className="h-8" type="date" value={String(filter.value ?? '')} onChange={(e) => onChange(e.target.value)} />
  }
  return <Input className="h-8" type={kind === 'number' ? 'number' : 'text'} value={String(filter.value ?? '')} onChange={(e) => onChange(coerce(e.target.value, kind))} />
}

export function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border p-3 text-sm hover:bg-accent/20">
      <input type="checkbox" className="mt-0.5 size-4 accent-primary" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="grid gap-0.5"><span className="font-medium">{label}</span>{hint && <span className="text-xs text-muted-foreground">{hint}</span>}</span>
    </label>
  )
}

export function NumberSetting({ label, hint, value, onChange, min, max, step, suffix }: {
  label: string; hint?: string; value: number | string; onChange: (v: string) => void; min?: number; max?: number; step?: number; suffix?: string
}) {
  return (
    <div className="grid gap-1.5 rounded-xl border border-border p-3 text-sm">
      <span className="font-medium">{label}</span>
      <div className="flex items-center gap-2">
        <Input className="h-8 w-28" type="number" value={value} min={min} max={max} step={step} onChange={(e) => onChange(e.target.value)} />
        {suffix && <span className="text-xs text-muted-foreground">{suffix}</span>}
      </div>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  )
}

export type { Draft }

/** A published dashboard analysis to watch: dashboard, analysis, the table it returns, and the dashboard's filters. */
export function AnalysisPicker({ analyses, error, value, onChange, copy }: {
  analyses: DashboardAnalysis[]; error?: string | null; copy: Copy
  value: { id: string; computation: string; table: string; filters: Record<string, string> }
  onChange: (v: { id: string; computation: string; table: string; filters: Record<string, string> }) => void
}) {
  if (!analyses.length) {
    return <p className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">{error || copy.no_analyses}</p>
  }
  const picked = analyses.find((a) => a.publication_id === value.id)
  const computation = picked?.computations.find((c) => c.name === value.computation)
  return (
    <div className="grid gap-3">
      <div className="grid gap-2">
        {analyses.map((a) => (
          <div key={a.publication_id} className={cn('grid gap-2 rounded-xl border p-3', a.publication_id === value.id ? 'border-primary/50 bg-primary/5' : 'border-border')}>
            <span className="flex items-center gap-2 text-sm font-medium"><LayoutDashboard className="size-4 text-muted-foreground" aria-hidden />{a.title}</span>
            <div className="flex flex-wrap gap-1.5">
              {a.computations.map((c) => {
                const on = a.publication_id === value.id && c.name === value.computation
                return (
                  <button key={c.name} type="button" aria-pressed={on} title={c.cards.join(' · ')}
                    onClick={() => onChange({ id: a.publication_id, computation: c.name, table: c.tables[0] ?? 'main', filters: a.publication_id === value.id ? value.filters : {} })}
                    className={cn('grid rounded-lg border px-2.5 py-1.5 text-left text-xs transition-colors',
                      on ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-foreground/20')}>
                    <span className="font-mono font-medium">{c.name}</span>
                    {c.cards.length > 0 && <span className="max-w-[16rem] truncate text-muted-foreground">{c.cards.slice(0, 2).join(' · ')}</span>}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>
      {error && <p className="text-xs text-muted-foreground">{error}</p>}
      {picked && computation && (
        <div className="grid gap-3 @2xl:grid-cols-2">
          {computation.tables.length > 1 && (
            <Labeled label={copy.analysis_table}>
              <FdeSelect value={value.table} ariaLabel={copy.analysis_table} required options={computation.tables.map((t) => ({ value: t, label: t }))}
                onValueChange={(table) => onChange({ ...value, table })} />
            </Labeled>
          )}
          {picked.filters.map((f) => (
            <Labeled key={f.name} label={f.label || f.name} hint={f.default != null ? `${copy.analysis_default}: ${String(f.default)}` : undefined}>
              {Array.isArray(f.options) && f.options.length > 0 ? (
                <FdeSelect value={value.filters[f.name] ?? ''} ariaLabel={f.label || f.name}
                  options={[{ value: '', label: copy.analysis_use_default }, ...f.options.map((o) => ({ value: String(o), label: String(o) }))]}
                  onValueChange={(v) => onChange({ ...value, filters: { ...value.filters, [f.name]: v } })} />
              ) : (
                <Input value={value.filters[f.name] ?? ''} placeholder={copy.analysis_use_default}
                  onChange={(e) => onChange({ ...value, filters: { ...value.filters, [f.name]: e.target.value } })} />
              )}
            </Labeled>
          ))}
        </div>
      )}
      {picked && <p className="text-xs text-muted-foreground">{copy.analysis_pinned}</p>}
    </div>
  )
}
